import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension' // required for devtools typing
import type {
  Requirement,
  CreateRequirement,
  UpdateRequirement,
  RequirementGenerationJob,
  RequirementType,
  RequirementStatus,
} from '@/lib/schemas'
import { api } from '@/lib/api'

// ===================
// Types
// ===================

interface RequirementsState {
  // State
  requirements: Requirement[]
  generationJob: RequirementGenerationJob | null
  isLoading: boolean
  isGenerating: boolean
  error: string | null
  currentBidId: string | null

  // Actions - Fetching
  fetchRequirements: (bidId: string) => Promise<void>

  // Actions - CRUD
  createRequirement: (bidId: string, data: Omit<CreateRequirement, 'bidId'>) => Promise<Requirement>
  updateRequirement: (id: string, data: Partial<UpdateRequirement>) => Promise<Requirement>
  deleteRequirement: (id: string) => Promise<void>
  bulkUpdateStatus: (ids: string[], status: RequirementStatus) => Promise<void>

  // Actions - Generation
  generateRequirements: (bidId: string) => Promise<RequirementGenerationJob>
  pollGenerationJob: (bidId: string, jobId: string) => void
  stopPollingGeneration: () => void
  regenerateRequirements: (bidId: string, keepManual: boolean) => Promise<RequirementGenerationJob>

  // Actions - Utility
  clearError: () => void
  reset: () => void

  // Computed
  getRequirementsByType: (type: RequirementType) => Requirement[]
  getRequirementsByStatus: (status: RequirementStatus) => Requirement[]
  getComplianceStats: () => {
    total: number
    compliant: number
    partial: number
    nonCompliant: number
    pending: number
    notApplicable: number
  }
}

// Polling configuration
const POLL_INTERVAL = 2000 // 2 seconds
const MAX_POLL_ATTEMPTS = 450 // 15 minutes max (matches Lambda timeout of 900s)

// Store polling state outside of Zustand
let generationPollingTimeoutId: ReturnType<typeof setTimeout> | null = null
let generationPollAttempts = 0
let lastSeenProgress = { documentsProcessed: 0, requirementsFound: 0 }
let staleProgressCount = 0
const MAX_STALE_POLLS = 450 // Match MAX_POLL_ATTEMPTS - backend doesn't report incremental progress

export const useRequirementsStore = create<RequirementsState>()(
  devtools(
    (set, get) => ({
      // Initial state
      requirements: [],
      generationJob: null,
      isLoading: false,
      isGenerating: false,
      error: null,
      currentBidId: null,

      // Fetch requirements for a bid
      fetchRequirements: async (bidId: string) => {
        // If switching bids while generating, stop polling and reset generation state
        const { currentBidId, isGenerating } = get()
        if (currentBidId && currentBidId !== bidId && isGenerating) {
          get().stopPollingGeneration()
          set({ isGenerating: false, generationJob: null })
        }

        try {
          set({ isLoading: true, error: null, currentBidId: bidId })
          const requirements = await api.get<Requirement[]>(`/bids/${bidId}/requirements`)
          set({ requirements, isLoading: false })

          // Resume polling if a job was in progress before page refresh
          if (!get().isGenerating) {
            const savedJobId = sessionStorage.getItem(`generation-job-${bidId}`)
            if (savedJobId) {
              try {
                const job = await api.get<RequirementGenerationJob>(
                  `/bids/${bidId}/requirements/jobs/${savedJobId}`
                )
                if (job.status === 'processing') {
                  set({ isGenerating: true, generationJob: job })
                  get().pollGenerationJob(bidId, savedJobId)
                } else {
                  // Job finished while we were away — clean up
                  sessionStorage.removeItem(`generation-job-${bidId}`)
                }
              } catch {
                // Job not found or error — clean up
                sessionStorage.removeItem(`generation-job-${bidId}`)
              }
            }
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch requirements',
            isLoading: false,
          })
        }
      },

      // Create a new requirement (manual)
      createRequirement: async (bidId: string, data: Omit<CreateRequirement, 'bidId'>) => {
        try {
          set({ isLoading: true, error: null })
          const requirement = await api.post<Requirement>(`/bids/${bidId}/requirements`, {
            ...data,
            bidId,
            extractionSource: 'manual',
          })
          set((state) => ({
            requirements: [...state.requirements, requirement],
            isLoading: false,
          }))
          return requirement
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to create requirement',
            isLoading: false,
          })
          throw error
        }
      },

      // Update a requirement
      updateRequirement: async (id: string, data: Partial<UpdateRequirement>) => {
        try {
          set({ error: null })
          const requirement = await api.patch<Requirement>(`/requirements/${id}`, data)
          set((state) => ({
            requirements: state.requirements.map((r) => (r.id === id ? requirement : r)),
          }))
          return requirement
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to update requirement',
          })
          throw error
        }
      },

      // Delete a requirement
      deleteRequirement: async (id: string) => {
        try {
          set({ error: null })
          await api.delete(`/requirements/${id}`)
          set((state) => ({
            requirements: state.requirements.filter((r) => r.id !== id),
          }))
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to delete requirement',
          })
          throw error
        }
      },

      // Bulk update status
      bulkUpdateStatus: async (ids: string[], status: RequirementStatus) => {
        try {
          set({ isLoading: true, error: null })
          await api.post<void>('/requirements/bulk-status', { ids, status })
          set((state) => ({
            requirements: state.requirements.map((r) =>
              ids.includes(r.id) ? { ...r, status } : r
            ),
            isLoading: false,
          }))
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to update requirements',
            isLoading: false,
          })
          throw error
        }
      },

      // Generate requirements from documents
      generateRequirements: async (bidId: string) => {
        try {
          set({ isGenerating: true, error: null, currentBidId: bidId })
          const job = await api.post<RequirementGenerationJob>(
            `/bids/${bidId}/requirements/generate`
          )
          set({ generationJob: job })

          // Persist job ID for resume-on-refresh
          sessionStorage.setItem(`generation-job-${bidId}`, job.id)

          // Start polling for job status
          get().pollGenerationJob(bidId, job.id)

          return job
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to start generation',
            isGenerating: false,
          })
          throw error
        }
      },

      // Poll generation job status
      pollGenerationJob: (bidId: string, jobId: string) => {
        // Clear any existing poll
        if (generationPollingTimeoutId) {
          clearTimeout(generationPollingTimeoutId)
          generationPollingTimeoutId = null
        }

        // Reset poll state
        generationPollAttempts = 0
        lastSeenProgress = { documentsProcessed: 0, requirementsFound: 0 }
        staleProgressCount = 0

        const poll = async () => {
          const { currentBidId } = get()

          // Stop if bid changed — reset generation state so other bid isn't affected
          if (currentBidId !== bidId) {
            get().stopPollingGeneration()
            set({ isGenerating: false, generationJob: null })
            return
          }

          // Stop if max attempts reached (hard limit)
          if (generationPollAttempts >= MAX_POLL_ATTEMPTS) {
            console.warn('[requirements-store] Max poll attempts reached, stopping polling')
            const { requirements } = get()
            set({
              isGenerating: false,
              error: `Extraction timed out. ${requirements.length} requirements from previous run are available. Refresh to check if extraction completed.`,
            })
            get().stopPollingGeneration()
            return
          }

          // Stop if no progress for too long
          if (staleProgressCount >= MAX_STALE_POLLS) {
            console.warn('[requirements-store] No progress detected, stopping polling')
            const { generationJob } = get()
            set({
              isGenerating: false,
              error: `Generation appears stalled (no progress for 60s). Found ${generationJob?.requirementsFound ?? 0} requirements. Try refreshing the page.`,
            })
            get().stopPollingGeneration()
            return
          }

          try {
            generationPollAttempts++

            // Fetch job status
            const job = await api.get<RequirementGenerationJob>(
              `/bids/${bidId}/requirements/jobs/${jobId}`
            )
            set({ generationJob: job })

            // Check for progress
            const hasProgress =
              job.documentsProcessed > lastSeenProgress.documentsProcessed ||
              job.requirementsFound > lastSeenProgress.requirementsFound

            if (hasProgress) {
              lastSeenProgress = {
                documentsProcessed: job.documentsProcessed,
                requirementsFound: job.requirementsFound,
              }
              staleProgressCount = 0 // Reset stale counter on progress
            } else {
              staleProgressCount++
            }

            if (job.status === 'completed') {
              // Re-fetch job to ensure accurate final counts (the initial job has 0 counts)
              const finalJob = await api.get<RequirementGenerationJob>(
                `/bids/${bidId}/requirements/jobs/${jobId}`
              )

              // Fetch the generated requirements
              const requirements = await api.get<Requirement[]>(`/bids/${bidId}/requirements`)
              set({
                generationJob: finalJob,
                requirements,
                isGenerating: false,
              })
              get().stopPollingGeneration()
            } else if (job.status === 'failed') {
              set({
                isGenerating: false,
                error: job.errorMessage || 'Generation failed',
              })
              get().stopPollingGeneration()
            } else {
              // Still processing, continue polling
              generationPollingTimeoutId = setTimeout(poll, POLL_INTERVAL)
            }
          } catch (error) {
            console.error('[requirements-store] Poll error:', error)
            // Continue polling on transient errors
            staleProgressCount++ // Count network errors as stale
            if (generationPollAttempts < MAX_POLL_ATTEMPTS && staleProgressCount < MAX_STALE_POLLS) {
              generationPollingTimeoutId = setTimeout(poll, POLL_INTERVAL)
            } else {
              set({
                isGenerating: false,
                error: 'Failed to check generation status. Try refreshing the page.',
              })
              get().stopPollingGeneration()
            }
          }
        }

        // Start polling
        void poll()
      },

      // Stop polling for generation status
      stopPollingGeneration: () => {
        if (generationPollingTimeoutId) {
          clearTimeout(generationPollingTimeoutId)
          generationPollingTimeoutId = null
        }
        generationPollAttempts = 0
        staleProgressCount = 0
        lastSeenProgress = { documentsProcessed: 0, requirementsFound: 0 }
        // Clean up persisted job ID
        const { currentBidId } = get()
        if (currentBidId) {
          sessionStorage.removeItem(`generation-job-${currentBidId}`)
        }
      },

      // Regenerate requirements (with option to keep manual ones)
      regenerateRequirements: async (bidId: string, keepManual: boolean) => {
        try {
          set({ isGenerating: true, error: null, currentBidId: bidId })
          const job = await api.post<RequirementGenerationJob>(
            `/bids/${bidId}/requirements/regenerate`,
            { keepManual }
          )
          set({ generationJob: job })

          // Persist job ID for resume-on-refresh
          sessionStorage.setItem(`generation-job-${bidId}`, job.id)

          // Start polling for job status
          get().pollGenerationJob(bidId, job.id)

          return job
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to start regeneration',
            isGenerating: false,
          })
          throw error
        }
      },

      // Clear error
      clearError: () => {
        set({ error: null })
      },

      // Reset store state
      reset: () => {
        get().stopPollingGeneration()
        set({
          requirements: [],
          generationJob: null,
          isLoading: false,
          isGenerating: false,
          error: null,
          currentBidId: null,
        })
      },

      // Get requirements by type
      getRequirementsByType: (type: RequirementType) => {
        return get().requirements.filter((r) => r.type === type)
      },

      // Get requirements by status
      getRequirementsByStatus: (status: RequirementStatus) => {
        return get().requirements.filter((r) => r.status === status)
      },

      // Get compliance statistics
      getComplianceStats: () => {
        const { requirements } = get()
        return {
          total: requirements.length,
          compliant: requirements.filter((r) => r.status === 'compliant').length,
          partial: requirements.filter((r) => r.status === 'partial').length,
          nonCompliant: requirements.filter((r) => r.status === 'non_compliant').length,
          pending: requirements.filter((r) => r.status === 'pending').length,
          notApplicable: requirements.filter((r) => r.status === 'not_applicable').length,
        }
      },
    }),
    { name: 'requirements-store' }
  )
)
